import React, { createContext, useContext, useState } from 'react';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { buildPalette, typographyOptions, radius, shadow, ACCENTS, coolSlate } from '../theme';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

const createAppTheme = (isDark: boolean): Theme => {
  const sl = isDark ? coolSlate.dark : coolSlate.light;
  const acc = isDark ? ACCENTS.indigo.dark : ACCENTS.indigo.light;
  const sh = isDark ? shadow.dark : shadow.light;

  return createTheme({
    palette: buildPalette(isDark),
    typography: typographyOptions,
    shape: {
      borderRadius: radius.default,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: sl.bg,
            backgroundImage: isDark
              ? `radial-gradient(ellipse at center, ${sl.surface} 0%, ${sl.bg} 100%)`
              : `radial-gradient(ellipse at center, #FFFFFF 0%, ${sl.bg} 100%)`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: sl.surface,
            border: `1px solid ${sl.line}`,
            backgroundImage: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isDark
                ? `0px 10px 20px ${acc.accent}1A, 0px 6px 6px rgba(0,0,0,0.23)`
                : `0px 10px 20px ${acc.accent}26, 0px 6px 6px rgba(0,0,0,0.1)`,
              borderColor: `${acc.accent}40`,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.default,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.78125rem',
            padding: '7px 12px',
            transition: 'all 0.2s ease-in-out',
          },
          contained: {
            background: `linear-gradient(135deg, ${acc.accent} 0%, ${acc.hover} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${isDark ? ACCENTS.indigo.light.accent : ACCENTS.indigo.dark.accent} 0%, ${acc.accent} 100%)`,
              transform: 'translateY(-1px)',
              boxShadow: `0px 6px 12px ${acc.accent}40`,
            },
          },
          outlined: {
            borderColor: sl.line,
            '&:hover': {
              borderColor: acc.accent,
              backgroundColor: acc.tint,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontSize: '0.75rem',
            fontWeight: 500,
          },
          colorPrimary: {
            backgroundColor: acc.tint,
            color: acc.accent,
            border: `1px solid ${acc.soft}`,
          },
          colorSecondary: {
            backgroundColor: 'rgba(102, 187, 106, 0.12)',
            color: '#81C784',
            border: '1px solid rgba(102, 187, 106, 0.24)',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: radius.bar,
            height: 6,
            backgroundColor: isDark ? sl.surface2 : sl.line,
          },
          bar: {
            borderRadius: radius.bar,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            border: `1px solid ${sl.line}`,
            borderRadius: radius.lg,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? sl.sidebar : sl.bg,
            '& .MuiTableCell-head': {
              backgroundColor: 'transparent',
              fontWeight: 600,
              fontSize: '0.6875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: isDark ? sl.ink2 : sl.ink3,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: sl.line,
            padding: '12px 16px',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: radius.default,
            fontSize: '0.875rem',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: '0.875rem',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.default,
            '&:hover': {
              backgroundColor: acc.tint,
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? sl.surface2 : sl.line,
            '&::after': {
              background: isDark
                ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            },
          },
        },
      },
      // Intentional v2 addition: suppress MUI's dark-mode gradient on Paper and
      // apply the v2 shadow scale. This affects menus, dropdowns, and modals —
      // a deliberate global change for the v2 shell aesthetic.
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: sh.card,
          },
          elevation1: {
            boxShadow: sh.card,
          },
          elevation8: {
            boxShadow: sh.pop,
          },
        },
      },
    },
  });
};

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('theme-mode');
      if (saved) return saved === 'dark';
    } catch {
      // localStorage unavailable (e.g. test environment)
    }
    return false;
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      try {
        localStorage.setItem('theme-mode', next ? 'dark' : 'light');
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  };

  const theme = createAppTheme(isDarkMode);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
